import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Key,
  Plus,
  RefreshCw,
  Settings,
  History,
  Copy,
  CheckCircle,
  XCircle,
  Trash2,
  Edit,
  Eye,
  AlertCircle,
  Users,
  Activity,
  Shield
} from 'lucide-react'
import { apiClientsApi, ApiClient, ApiAccessLog } from '@/lib/api'
import { cn } from '@/lib/utils'
import ApiClientModal from '@/components/ApiClientModal'
import ApiClientLogsModal from '@/components/ApiClientLogsModal'

export default function ApiClientsPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'clients' | 'logs'>('clients')
  const [showModal, setShowModal] = useState(false)
  const [editingClient, setEditingClient] = useState<ApiClient | null>(null)
  const [logsClient, setLogsClient] = useState<ApiClient | null>(null)
  const [copiedToken, setCopiedToken] = useState<number | null>(null)

  // Fetch clients
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['api-clients'],
    queryFn: apiClientsApi.getAll,
    refetchInterval: 30000
  })

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['api-clients-stats'],
    queryFn: apiClientsApi.getStats,
    refetchInterval: 30000
  })

  // Fetch all logs
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['api-clients-logs'],
    queryFn: () => apiClientsApi.getAllLogs(100),
    enabled: activeTab === 'logs'
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: apiClientsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] })
      queryClient.invalidateQueries({ queryKey: ['api-clients-stats'] })
    }
  })

  // Regenerate token mutation
  const regenerateTokenMutation = useMutation({
    mutationFn: apiClientsApi.regenerateToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] })
    }
  })

  const clients = clientsData?.clients || []
  const logs = logsData?.logs || []
  const stats = statsData?.stats

  const copyToClipboard = async (text: string, clientId: number) => {
    await navigator.clipboard.writeText(text)
    setCopiedToken(clientId)
    setTimeout(() => setCopiedToken(null), 2000)
  }

  const formatDateTime = (isoString: string | null) => {
    if (!isoString) return '-'
    const date = new Date(isoString)
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDate = (isoString: string | null) => {
    if (!isoString) return 'Sin limite'
    const date = new Date(isoString)
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const isExpired = (fecha: string | null) => {
    if (!fecha) return false
    return new Date(fecha) < new Date()
  }

  const getStatusBadge = (client: ApiClient) => {
    if (!client.activo) {
      return <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600">Inactivo</span>
    }
    if (isExpired(client.fecha_expiracion)) {
      return <span className="px-2 py-1 text-xs rounded-full bg-danger-100 text-danger-700">Expirado</span>
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-success-100 text-green-700">Activo</span>
  }

  const getAccessStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <span className="px-2 py-1 text-xs rounded-full bg-success-100 text-green-700">Exito</span>
      case 'invalid_token':
        return <span className="px-2 py-1 text-xs rounded-full bg-danger-100 text-danger-700">Token invalido</span>
      case 'inactive':
        return <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600">Inactivo</span>
      case 'expired':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">Expirado</span>
      case 'ip_not_allowed':
        return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700">IP no permitida</span>
      case 'rate_limited':
        return <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">Limite alcanzado</span>
      case 'error':
        return <span className="px-2 py-1 text-xs rounded-full bg-danger-100 text-danger-700">Error</span>
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600">{status}</span>
    }
  }

  const handleEdit = (client: ApiClient) => {
    setEditingClient(client)
    setShowModal(true)
  }

  const handleCreate = () => {
    setEditingClient(null)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingClient(null)
  }

  const handleDelete = async (client: ApiClient) => {
    if (confirm(`Eliminar cliente "${client.nombre}"? Esta accion no se puede deshacer.`)) {
      deleteMutation.mutate(client.id)
    }
  }

  const handleRegenerateToken = async (client: ApiClient) => {
    if (confirm(`Regenerar token para "${client.nombre}"? El token actual dejara de funcionar.`)) {
      regenerateTokenMutation.mutate(client.id)
    }
  }

  const renderStatsCards = () => {
    if (!stats) return null

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total_clients}</p>
              <p className="text-sm text-slate-500">Clientes totales</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-success-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.active_clients}</p>
              <p className="text-sm text-slate-500">Clientes activos</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Activity className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.today_accesses}</p>
              <p className="text-sm text-slate-500">Accesos hoy</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Shield className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.today_successful}</p>
              <p className="text-sm text-slate-500">Exitosos hoy</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderClientsTable = () => {
    if (clientsLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )
    }

    if (clients.length === 0) {
      return (
        <div className="text-center py-12">
          <Key className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No hay clientes API</h3>
          <p className="text-slate-500 mb-4">Crea un cliente para generar un token de acceso al catalogo</p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nuevo Cliente
          </button>
        </div>
      )
    }

    return (
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">URL Catalogo</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Estado</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Expira</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Limite/dia</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Accesos hoy</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {clients.map((client) => (
              <tr key={client.id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div>
                    <p className="font-medium text-gray-900">{client.nombre}</p>
                    {client.email && (
                      <p className="text-sm text-slate-500">{client.email}</p>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-gray-800 text-cyan-400 px-2 py-1 rounded font-mono max-w-xs truncate" title={apiClientsApi.getExportUrl(client.token)}>
                      {apiClientsApi.getExportUrl(client.token)}
                    </code>
                    <button
                      onClick={() => copyToClipboard(apiClientsApi.getExportUrl(client.token), client.id)}
                      className="p-1 text-slate-500 hover:text-gray-700 hover:bg-slate-100 rounded"
                      title="Copiar URL completa"
                    >
                      {copiedToken === client.id ? (
                        <CheckCircle className="h-4 w-4 text-success-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(client)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {formatDate(client.fecha_expiracion)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {client.limite_descargas_dia === 0 ? 'Ilimitado' : client.limite_descargas_dia}
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-gray-900">
                    {client.accesos_hoy || 0}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => setLogsClient(client)}
                      className="p-2 text-slate-500 hover:text-gray-700 hover:bg-slate-100 rounded-lg"
                      title="Ver logs"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(client)}
                      className="p-2 text-slate-500 hover:text-gray-700 hover:bg-slate-100 rounded-lg"
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleRegenerateToken(client)}
                      className="p-2 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded-lg"
                      title="Regenerar token"
                      disabled={regenerateTokenMutation.isPending}
                    >
                      <RefreshCw className={cn("h-4 w-4", regenerateTokenMutation.isPending && "animate-spin")} />
                    </button>
                    <button
                      onClick={() => handleDelete(client)}
                      className="p-2 text-danger-500 hover:text-danger-700 hover:bg-red-50 rounded-lg"
                      title="Eliminar"
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderLogsTable = () => {
    if (logsLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )
    }

    if (logs.length === 0) {
      return (
        <div className="text-center py-12 text-slate-500">
          <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p>No hay registros de acceso</p>
        </div>
      )
    }

    return (
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">IP</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Estado</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tiempo</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {logs.map((log) => (
              <tr key={log.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-sm text-slate-600">
                  {formatDateTime(log.timestamp)}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {log.cliente_nombre || '-'}
                </td>
                <td className="px-4 py-3">
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded">{log.ip}</code>
                </td>
                <td className="px-4 py-3">
                  {getAccessStatusBadge(log.status)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {log.response_time_ms ? `${log.response_time_ms}ms` : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Catalogo</h1>
          <p className="text-slate-500">Gestiona clientes externos con acceso al catalogo de productos</p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nuevo Cliente
        </button>
      </div>

      {/* Stats */}
      {renderStatsCards()}

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('clients')}
            className={cn(
              'pb-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === 'clients'
                ? 'border-blue-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-gray-700'
            )}
          >
            <Settings className="h-4 w-4 inline mr-2" />
            Clientes
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={cn(
              'pb-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === 'logs'
                ? 'border-blue-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-gray-700'
            )}
          >
            <History className="h-4 w-4 inline mr-2" />
            Historial de Accesos
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'clients' && renderClientsTable()}
      {activeTab === 'logs' && renderLogsTable()}

      {/* Modals */}
      {showModal && (
        <ApiClientModal
          client={editingClient}
          onClose={handleCloseModal}
        />
      )}

      {logsClient && (
        <ApiClientLogsModal
          client={logsClient}
          onClose={() => setLogsClient(null)}
        />
      )}
    </div>
  )
}
